import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  Icon,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import DialogBase from './DialogBase';
import ProgressBar from '../ProgressBar';
import ErrorMessage from '../ErrorMessage';

import { Organisatie } from '../../../models/organisatie/organisatie';
import { ListParams } from '../../../models/listParams';
import { authService } from '../../../services/auth.service';
import { planManagementService } from '../../../services/plan-management.service';
import { userManagementService } from '../../../services/user-management.service';

interface DialogShareAssetsProps {
  open: boolean;
  assetLayerId?: string;
  allowMultiple?: boolean;
  groupBySource?: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

interface OrganizationOption {
  id: string;
  label: string;
  group: 'Met gebied' | 'Zonder gebied';
}

const REFRESH_DELAY_MS = 750;

const DialogShareAssets: React.FC<DialogShareAssetsProps> = ({
  open,
  assetLayerId,
  allowMultiple = true,
  groupBySource = true,
  onClose,
  onSubmit,
}) => {
  const navigate = useNavigate();

  const [options, setOptions] = useState<OrganizationOption[]>([]);
  const [knownOptions, setKnownOptions] = useState<OrganizationOption[]>([]);
  const [sharedOrganizationIds, setSharedOrganizationIds] = useState<string[]>([]);
  const [assetLayerName, setAssetLayerName] = useState<string>('');
  const [usePlanShareSettings, setUsePlanShareSettings] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<OrganizationOption[]>([]);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const ownOrganisationId = userManagementService.getStoredOrganisatie()?.id;

  const autocompleteValue = allowMultiple
    ? selectedOptions
    : (selectedOptions[0] ?? null);

  const loadData = useCallback(async () => {
    if (!assetLayerId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const isAdmin = await authService.isAdmin();
      setIsAdmin(isAdmin);

      const storedOrganisatie = userManagementService.getStoredOrganisatie();

      const params: ListParams = {
        offset: 0,
        limit: 250,
      };

      const [data, assetLayer] = await Promise.all([
        planManagementService.getOrganisatiesToShare(params, true, { fetchAll: true }),
        planManagementService.getAssetLayer(assetLayerId),
      ]);

      let organisatieItems: Organisatie[] = Array.isArray(data?.items) ? data.items : [];

      if (storedOrganisatie?.id) {
        organisatieItems = organisatieItems.filter((x: Organisatie) => x.id !== storedOrganisatie.id);
      }

      const toOption = (organisatie: Organisatie): OrganizationOption | null => {
        if (!organisatie.id) {
          return null;
        }

        return {
          id: organisatie.id,
          label: organisatie.displayName || organisatie.name || organisatie.id,
          group: organisatie.organizationType === 'zonder-gebied' ? 'Zonder gebied' : 'Met gebied',
        };
      };

      const candidateOptions = organisatieItems
        .map(toOption)
        .filter((item): item is OrganizationOption => Boolean(item))
        .sort((a, b) => {
          if (a.group !== b.group) return a.group.localeCompare(b.group, 'nl-NL');
          return a.label.localeCompare(b.label, 'nl-NL');
        });

      const allKnownOptions = (Array.isArray(data?.items) ? data.items as Organisatie[] : [])
        .map(toOption)
        .filter((item): item is OrganizationOption => Boolean(item))
        .sort((a, b) => a.label.localeCompare(b.label, 'nl-NL'));

      const sharedIds = Array.isArray(assetLayer?.sharedWithOrganizationIds)
        ? assetLayer.sharedWithOrganizationIds.filter((item: unknown): item is string => typeof item === 'string' && Boolean(item))
        : [];
      const resolvedAssetName = assetLayer?.displayName
        || assetLayer?.layerDisplayName
        || assetLayer?.name
        || assetLayerId
        || '';

      setOptions(candidateOptions);
      setKnownOptions(allKnownOptions);
      setSharedOrganizationIds(sharedIds);
      setAssetLayerName(resolvedAssetName);
      setUsePlanShareSettings(Boolean(assetLayer?.usePlanShareSettings));
      setSelectedOptions([]);
      setSelectionError(null);
    }
    catch (err: any) {
      if (err.message === 'Unable to refresh token') {
        navigate('/verlopen');
      }
      setError(err.message || 'Er is een fout opgetreden');
    }
    finally {
      setLoading(false);
    }
  }, [assetLayerId, navigate]);

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, loadData]);

  const sharedOrganizationIdSet = useMemo(() => {
    return new Set(sharedOrganizationIds);
  }, [sharedOrganizationIds]);

  const sharedOrganizations = useMemo(() => {
    return sharedOrganizationIds.map((organizationId) => {
      const mapped = knownOptions.find((option) => option.id === organizationId)
        || options.find((option) => option.id === organizationId);

      return mapped || {
        id: organizationId,
        label: organizationId,
      };
    });
  }, [knownOptions, options, sharedOrganizationIds]);

  const selectableOptions = useMemo(() => {
    return options.filter((option) => !sharedOrganizationIdSet.has(option.id));
  }, [options, sharedOrganizationIdSet]);

  const refreshAfterMutation = useCallback(async () => {
    await new Promise((resolve) => setTimeout(resolve, REFRESH_DELAY_MS));
    await loadData();
    onSubmit();
  }, [loadData, onSubmit]);

  const handleSubmit = async () => {
    if (!assetLayerId) {
      return;
    }

    if (usePlanShareSettings) {
      return;
    }

    if (selectedOptions.length === 0) {
      setSelectionError('Selecteer minimaal 1 organisatie.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSelectionError(null);

      const idsToAdd = selectedOptions
        .map((item) => item.id)
        .filter((organizationId) => !sharedOrganizationIdSet.has(organizationId));

      await Promise.all(
        idsToAdd.map((organizationId) =>
          planManagementService.addShareAssetLayerWithOrganisation(assetLayerId, organizationId)
        )
      );

      await refreshAfterMutation();
    }
    catch (err: any) {
      console.error('Error updating asset sharing:', err);
      setError(err?.message || 'Er is een fout opgetreden');
    }
    finally {
      setSaving(false);
    }
  };

  const removeSharedOrganization = useCallback(async (organizationId: string) => {
    if (!assetLayerId || usePlanShareSettings) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      await planManagementService.deleteShareAssetLayerWithOrganisation(assetLayerId, organizationId);
      await refreshAfterMutation();
    }
    catch (err: any) {
      console.error('Error removing asset sharing:', err);
      setError(err?.message || 'Er is een fout opgetreden');
    }
    finally {
      setSaving(false);
    }
  }, [assetLayerId, refreshAfterMutation, usePlanShareSettings]);

  return (
    <DialogBase
      open={open}
      title={usePlanShareSettings ? `${assetLayerName} gedeeld met organisaties via plan deelinstellingen` : `${assetLayerName} gedeeld met organisaties specifiek beheren`}
      contentText={usePlanShareSettings
        ? 'Deze kaartlaag gebruikt plan deelinstellingen. Je kunt hieronder alleen bekijken met welke organisaties de kaartlaag gedeeld wordt.'
        : 'Voeg organisaties toe of verwijder bestaande organisaties voor deze kaartlaag.'}
      onClose={onClose}
      width="900px"
      minWidth="700px"
    >
      {!usePlanShareSettings && (
        <>
          <FormControl fullWidth sx={{ my: 1 }}>
            <Autocomplete
              multiple={allowMultiple}
              options={selectableOptions}
              value={autocompleteValue}
              disabled={loading || saving}
              noOptionsText="Geen keuzes beschikbaar"
              loadingText="Laden..."
              onChange={(_event, value) => {
                const normalized = Array.isArray(value) ? value : value ? [value] : [];
                setSelectedOptions(normalized);
                if (normalized.length > 0) {
                  setSelectionError(null);
                }
              }}
              groupBy={groupBySource ? (option) => option.group : undefined}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterSelectedOptions={allowMultiple}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="filled" label={option.label} {...getTagProps({ index })} key={option.id} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="Organisaties toevoegen"
                  variant="filled"
                  placeholder="Zoek en selecteer één of meerdere organisaties"
                  error={Boolean(selectionError)}
                  helperText={selectionError || undefined}
                />
              )}
            />
          </FormControl>

          <Box sx={{ display: "flex", my: 0.5 }}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Icon>add</Icon>}
              onClick={handleSubmit}
              disabled={saving || selectedOptions.length === 0}
              sx={{ width: "auto" }}
            >
              Toevoegen
            </Button>
          </Box>
          <Divider sx={{ my: 2 }} />
        </>
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
        Geopackage: <strong>{assetLayerName || '-'}</strong>
      </Typography>

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Geopackage is gedeeld met organisaties ({sharedOrganizationIds.length})
      </Typography>

      <List dense>
        {sharedOrganizations.map((organization) => {
          const isOwnOrganisation = organization.id === ownOrganisationId;
          return (
            <ListItem
              key={organization.id}
              secondaryAction={!isOwnOrganisation && !usePlanShareSettings ? (
                <Tooltip title="Organisatie verwijderen" placement="right" arrow>
                  <IconButton
                    edge="end"
                    disabled={loading || saving}
                    onClick={() => removeSharedOrganization(organization.id)}
                  >
                    <Icon>delete</Icon>
                  </IconButton>
                </Tooltip>
              ) : null}
            >
              <ListItemText
                primary={
                  isOwnOrganisation ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {organization.label}
                      <Chip label="Eigen organisatie" color="primary" variant="outlined" size="small" />
                    </div>
                  ) : (
                    organization.label
                  )
                }
                secondary={`${'group' in organization ? organization.group : 'Met gebied'}${isAdmin && organization.id ? ` (${organization.id})` : ''}`}
              />
            </ListItem>
          );
        })}

        {sharedOrganizations.length === 0 && (
          <ListItem>
            <ListItemText primary="Nog geen organisaties" />
          </ListItem>
        )}
      </List>

      {(loading || saving) && <ProgressBar />}
      {error && <ErrorMessage message={error} />}
    </DialogBase>
  );
};

export default DialogShareAssets;